import { useEffect, useId, useState } from "react";
import { ChevronDown, Link as LinkIcon, Search, X } from "lucide-react";

/** Embedded FL summary returned inline by /assets list + detail endpoints
 *  (backend `AssetResponse.functional_location`). Lets callers render FL
 *  name/code without a second /functional-locations fetch. */
export interface AssetFlocSummary {
  id: string;
  code: string;
  name: string;
  level: number;
  description?: string | null;
}

export interface AssetOption {
  id: string;
  name: string;
  asset_code: string;
  // Optional — populated by callers that hydrate from /assets. Existing
  // callers that ignore this still typecheck.
  functional_location_id?: string | null;
  // Inline FL summary (added to the backend response so the Maintenance /
  // Instrumentation tables don't need a separate FL fetch + client-side
  // join). Will be null when the asset has no FL or the caller fetched
  // from a path that doesn't hydrate it (POST/PUT response).
  functional_location?: AssetFlocSummary | null;
}

interface SearchableAssetPickerProps {
  assets: AssetOption[];
  /** Currently linked asset id, or "" / null for none. */
  value: string | null | undefined;
  onChange: (assetId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Visual label rendered above the field. */
  label?: string;
  /** Helper line under the field. */
  help?: string;
  /** Size variant for compact contexts (sidebar rows). */
  size?: "md" | "sm";
}

/** Typeahead asset picker backed by a native HTML `<datalist>`.
 *
 *  Earlier versions of this component used a custom dropdown rendered
 *  inline / portaled / etc. — every iteration ran into a different
 *  edge case (overflow clipping inside the sidebar, stacking-context
 *  issues, focus race conditions). The reliable solution is to delegate
 *  the dropdown to the browser: a `<datalist>` popup is rendered by the
 *  user agent itself, which means
 *    - it can't be clipped by any ancestor's `overflow: hidden/auto`
 *    - it positions correctly in every container (drawers, modals, tabs)
 *    - native typeahead filtering "just works" in Chrome/Firefox/Edge
 *
 *  Each `<option>` exposes `${asset_code} — ${name}` as its value. When
 *  the user picks one (or types it exactly), we look it up in the assets
 *  array and emit the corresponding asset id via `onChange`. Clearing
 *  the input emits `null` so the link can be detached. */
export function SearchableAssetPicker({
  assets,
  value,
  onChange,
  placeholder = "Type to search assets (e.g. pump)",
  disabled,
  label,
  help,
  size = "md",
}: SearchableAssetPickerProps) {
  const listId = useId();
  const [text, setText] = useState("");

  const selected = value ? assets.find((a) => a.id === value) ?? null : null;
  const selectedLabel = selected
    ? `${selected.asset_code} — ${selected.name}`
    : "";

  // Keep the input value in sync with the persisted selection so an
  // external change (e.g. polling tick on InlineSensorReadout, save in
  // SensorDetailDrawer) reflects in the input.
  useEffect(() => {
    setText(selectedLabel);
  }, [selectedLabel]);

  function handleInput(typed: string) {
    setText(typed);
    // A datalist pick fires onChange with the option's full `value`. An
    // exact-string match against the displayed label is therefore the
    // signal that the user committed a selection.
    const match = assets.find(
      (a) => `${a.asset_code} — ${a.name}` === typed,
    );
    if (match) {
      onChange(match.id);
      return;
    }
    // Empty input + previously-linked asset → detach.
    if (typed.trim() === "" && selected) {
      onChange(null);
      return;
    }
    // Anything else is in-flight typing; don't commit yet.
  }

  function clear() {
    if (disabled) return;
    setText("");
    onChange(null);
  }

  const sizeClass =
    size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm";

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-surface-200">
          {label}
        </label>
      )}
      <div
        className={
          "flex items-center gap-2 rounded-lg border bg-surface-900 " +
          sizeClass +
          (disabled
            ? " cursor-not-allowed opacity-60 border-surface-700"
            : " border-surface-700 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500")
        }
      >
        {selected ? (
          <LinkIcon className="h-3.5 w-3.5 shrink-0 text-sky-300" />
        ) : (
          <Search className="h-3.5 w-3.5 shrink-0 text-surface-500" />
        )}
        <input
          type="text"
          list={listId}
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-surface-100 placeholder:text-surface-500 focus:outline-none"
        />
        {selected && !disabled && (
          <button
            type="button"
            onClick={clear}
            className="rounded p-0.5 text-surface-400 hover:bg-white/5 hover:text-surface-200"
            title="Unlink asset"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-surface-500" />
      </div>
      {/* Native browser popup — appears below the input on click + typing,
          and can't be clipped by ancestor overflow. Each option's value is
          the displayed string so the browser's substring match works as
          users expect. */}
      <datalist id={listId}>
        {assets.map((a) => (
          <option key={a.id} value={`${a.asset_code} — ${a.name}`} />
        ))}
      </datalist>
      {help && <p className="mt-1 text-[10px] text-surface-500">{help}</p>}
      {assets.length === 0 && !disabled && (
        <p className="mt-1 text-[10px] text-amber-400">
          No assets in this site — create one on the Assets page first.
        </p>
      )}
    </div>
  );
}
